import { WhatsNewRelease, whatsNewReleases } from "../data/whatsNew";

/**
 * Which release notes a user has already read.
 *
 * Kept in localStorage rather than on the employee record: it is a "have you
 * seen this yet" marker, not a setting worth a round trip, and getting it wrong
 * costs someone one extra dialog. The key carries the signed-in email so a
 * shared machine does not swallow the notes for whoever logs in next.
 */

const STORAGE_PREFIX = "bamfords.whatsNewLastSeen";

function storageKey(): string {
  try {
    const email = localStorage.getItem("userEmail");
    return email ? `${STORAGE_PREFIX}.${email}` : STORAGE_PREFIX;
  } catch (error) {
    // Storage unavailable (private browsing).
    return STORAGE_PREFIX;
  }
}

/** Newest first. Sorted here so the data file can be edited in any order. */
export function getReleases(): WhatsNewRelease[] {
  return [...whatsNewReleases].sort((a, b) => b.date.localeCompare(a.date));
}

function readLastSeenVersion(): string | null {
  try {
    return localStorage.getItem(storageKey());
  } catch (error) {
    console.warn("Could not read the last seen release:", error);
    return null;
  }
}

/**
 * The releases this user has not read yet.
 *
 * A user who has never opened the dialog has read nothing, so their first
 * sight of it is the full history — which is the right thing to show someone
 * seeing the portal's notes for the first time. If the release they last read
 * has since been removed from the file, everything counts as unread rather
 * than nothing: better a dialog they have partly seen than one they never get.
 */
export function getUnreadReleases(): WhatsNewRelease[] {
  const releases = getReleases();
  const lastSeen = readLastSeenVersion();

  if (!lastSeen) {
    return releases;
  }

  const seenIndex = releases.findIndex((release) => release.version === lastSeen);
  return seenIndex === -1 ? releases : releases.slice(0, seenIndex);
}

export function hasUnreadReleases(): boolean {
  return getUnreadReleases().length > 0;
}

/**
 * Records that the user has now seen everything in the file. Called when the
 * dialog closes, however it was opened — reading the notes counts whether the
 * dialog arrived on its own or was asked for.
 */
export function markReleasesSeen(): void {
  const releases = getReleases();

  if (releases.length === 0) {
    return;
  }

  try {
    localStorage.setItem(storageKey(), releases[0].version);
  } catch (error) {
    console.warn("Could not record the last seen release:", error);
  }
}
